<template>
    <v-layout>
        <v-flex xs12 v-if="loading">
            <v-progress-linear :indeterminate="true"></v-progress-linear>
        </v-flex>
        <v-flex xs12  v-if="!loading&&error">
            <v-alert
                :value="error"
                type="error"
            >
                {{error}}
            </v-alert>
        </v-flex>
        <v-flex xs12 v-if="!loading&&!error">
            <v-card>
                <v-card-title class="headline primary white--text">
                    {{ 'Optimization Controls'|translate }}
                </v-card-title>
                <v-card-actions>
                       <span style="width:300px;"><v-text-field class="mr-4 ml-2" clearable v-model="search" type="text"
                                                                :label="'Keyword filter'|translate"></v-text-field></span>
                    <span style="width:200px;">
                        <v-text-field class="mr-4" clearable type="number" v-model.number="priorityFilter" :label="'Priority filter'|translate"></v-text-field>
                    </span>
                    <span style="width:300px;">
                        <v-autocomplete class="mr-4" clearable v-model="typeFilter" :label="'Type filter'|translate" :items="typeFilterOptions"></v-autocomplete>
                    </span>
                    <span style="width:300px;">
                        <v-autocomplete class="mr-4" clearable v-model="floorFilter" :label="'Floor filter'|translate" :items="floorFilterOptions"></v-autocomplete>
                    </span>
                    <v-spacer></v-spacer>
                </v-card-actions>

                <v-card-text>
                    <v-data-table
                        :headers="headers"
                        :items="refinedControlPoints"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                        :search="search2"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.sensor_name }}</td>
                            <td>{{ props.item.equipment_name }}</td>
                            <td>{{ props.item.equipment_type }}</td>
                            <td>{{ props.item.floor }}</td>
                            <td><span v-if="props.item.connector_id ">{{ getConnectorName(props.item.connector_id) }}</span></td>
                            <td>{{ props.item.haystack_id }}</td>
                            <td>{{ props.item.value }} <span v-if="props.item.unit&&props.item.unit!=='_'">{{ props.item.unit }}</span></td>
                            <td> <span v-if="props.item.meta.writable_priority_array">{{getWriteLabel(props.item.meta.writable_priority_array)}} </span>  </td>
                            <td><v-icon small @click="editItem(props.item)" v-if="$can('update', 'SoftwareSwitches')" class="ml-2">fa-edit</v-icon> </td>
                        </template>
                    </v-data-table>
                    <v-dialog v-model="editDialog" :max-width="1200">
                        <v-card v-if="editDialog&&editedItem">
                            <v-card-title class="primary white--text title">
                                {{editedItem.equipment_name}} - {{editedItem.sensor_name}}
                            </v-card-title>
                            <v-card-text>
                                <v-layout row wrap>
                                    <v-flex xs12>
                                        <data3-chart
                                                     :layout-overrides="layoutOverrides" :on-only="false"
                                                     :x-axis="'timestamp'"
                                                     disable-responsive
                                                     :show-export="false"
                                                     just-plot
                                                     :data-payload="chartPayload"
                                                     :date-range="dateRange"></data3-chart>
                                    </v-flex>
                                    <v-flex xs12 md6 v-if="editedItem.meta.writable_priority_array">
                                        <ol>
                                            <li v-for="(item,priority) in editedItem.meta.writable_priority_array">
                                                <span v-if="item">{{item.value}}  {{formatElderDate(item.deadline)}}
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on }">
                                                        <v-icon small v-on="on" class="ml-3"
                                                                @click="clearPriority(editedItem.sensor_id,priority+1)">fa-trash</v-icon>
                                                    </template>
                                                    <span>{{ 'Clear & Save'|translate }}</span>
                                                </v-tooltip>
                                                </span>
                                                <span v-if="!item">null</span>
                                            </li>
                                        </ol>
                                    </v-flex>
                                    <v-flex xs12 :class="editedItem.meta.writable_priority_array ? 'md6': ''">
                                        <v-text-field :label="'Sensor ID'|translate" v-model="editedItem.sensor_id" disabled></v-text-field>
                                        <v-text-field :label="'Upstream ID'|translate" v-model="editedItem.haystack_id" disabled></v-text-field>
                                        <v-text-field :label="'Value'|translate" v-model.number="editedItem.value" clearable :rules="editedItem.meta.writable_value_range ? [value => value < editedItem.meta.writable_value_range[0] || 'Min value is '+editedItem.meta.writable_value_range[0], value => value > editedItem.meta.writable_value_range[1] || 'Max value is '+editedItem.meta.writable_value_range[1]] : []"></v-text-field>
                                        <v-text-field :label="'Priority'|translate" v-model.number="editedItem.priority"></v-text-field>
                                        <v-text-field :label="'Duration (seconds)'|translate" v-model.number="editedItem.duration" clearable></v-text-field>
                                    </v-flex>
                                </v-layout>

                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn outline class="mr-2" @click="editDialog=false">{{'Cancel'|translate}}</v-btn>
                                <v-btn class="primary mr-2 elevation-4" @click="updateControl(editedItem.sensor_id,editedItem.value,editedItem.priority,editedItem.duration)" :disabled="!$can('update', 'SoftwareSwitches')">{{'Save'|translate}}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-card-text>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>

import {callAPI, getPoint, putOptimizationControl} from "@/helper";
import Data3Chart from "../components/Data3Chart.vue";
import _ from 'lodash'
import moment from "moment";
import {getConnector} from "../helper";

export default {
    name: 'OptimizationControls',
    components: {Data3Chart},
    data() {
        return {
            dialog: false,
            headers: [
                {text: this.$options.filters.translate('Name'), value: 'sensor_name'},
                {text: this.$options.filters.translate('Equipment Name'), value: 'equipment_name'},
                {text: this.$options.filters.translate('Equipment Type'), value: 'equipment_type'},
                {text: this.$options.filters.translate('Floor'), value: 'floor'},
                {text: this.$options.filters.translate('Connector'), value: 'connector_id'},
                {text: this.$options.filters.translate('Upstream ID'), value: 'haystack_id'},
                {text: this.$options.filters.translate('Current Value'), value: 'value'},
                {text: this.$options.filters.translate('Write'), sortable:false},
                {text: this.$options.filters.translate('Actions'), sortable: false},
            ],
            controlPoints: [],
            writablePoints: [],
            connectors: [],
            loading: true,
            interval: null,
            editedItem:null,
            editDialog:null,
            typeFilter:null,
            typeFilterOptions:[],
            floorFilterOptions:[],
            priorityFilter:null,
            floorFilter:null,
            search: '',
            search2: '',
            error:null,
            layoutOverrides:{
                xaxis: {
                    title: '',
                },
                yaxis:{
                    fixedrange: true
                },
                height:300,
                showlegend:false,
                legend: {
                    orientation:'h',
                    y: -0.2,
                },
                hoverlabel: {
                    namelength: -1
                },
                hovermode:"x",
            }
        }
    },
    computed: {
        dateRange(){
            if (!this.editedItem){
                return null
            }
            return {
                start:moment().add(1,"hour").startOf("hour").subtract(1, "days").format('YYYY-MM-DD HH:mm:ss'),
                end: moment().add(1,"hour").startOf("hour").format('YYYY-MM-DD HH:mm:ss')
            }
        },
        chartPayload(){
            if (!this.editedItem){
                return null
            }
            return [{
                filters: {
                    equipment_name: this.editedItem.equipment_name,
                    sensor_name: this.editedItem.sensor_name,
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                },
                pipeline: [],
                showlegend:false,
                seriesOverrides: {
                    type: 'scatter',
                    opacity: 1,
                    xbins: {
                        size: 1
                    },
                    ybins: {
                        size: 1
                    }
                },
            }]
        },
        refinedControlPoints() {
            let items = this.controlPoints
            if (this.typeFilter) {
                items = items.filter(x => x.equipment_type === this.typeFilter)
            }
            if (this.floorFilter) {
                items = items.filter(x => x.floor&&x.floor.startsWith(this.floorFilter))
            }
            if (this.priorityFilter) {
                items = items.filter(x => x.meta&&x.meta.writable_priority_array&&x.meta.writable_priority_array.slice(0,this.priorityFilter).some(item => item !== null))
            }
            return items
        },
    },
    mounted() {
        let me = this;
        me.refresh()
        me.interval = setInterval(function () {
            me.refresh()
        }, 30000);
    },
    watch: {
        search() {
            this.debounce(this.syncSearches, 500);
        }
    },
    beforeDestroy: function () {
        clearInterval(this.interval)
    },
    async beforeMount () {
        let pointsDf = await getPoint({
            facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
        })
        let writablePoints=_.filter(
            pointsDf['result'],
            function (x) {
                return x.meta&& x.meta.tags && x.meta.tags.includes('writable')
            })
        for (let point of writablePoints){
            let equipment = this.$store.state.pointsListStore.pointsList.equipment_details[point.equipment_name]
            if (equipment){
                point.equipment_type=equipment.equipment_type
                point.floor=equipment.floor
            }
        }
        this.floorFilterOptions=JSON.parse(JSON.stringify([...new Set(writablePoints.filter(x=>x.floor).map(x=>x.floor))])).sort()
        this.typeFilterOptions=JSON.parse(JSON.stringify([...new Set(writablePoints.filter(x=>x.equipment_type).map(x=>x.equipment_type))])).sort()
        this.writablePoints= writablePoints
        let df = await getConnector({
            facility_name: this.$store.state.pointsListStore.pointsList.facility_name
        })
        this.connectors = df['result']
    },
    methods: {
        openEquipmentCharts(item){
            let url="/equipment-plots?equipment="+item.equipment_name
            if (process.env.VUE_APP_SINGLE_WINDOW_MODE){
                window.location.href=url
            } else {
                window.open(url)
            }
        },
        getConnectorName(id){
            let found=this.connectors.filter(x=>x.connector_id===id)
            if (found&&found[0]){
                return found[0].type+ " - "+found[0].name
            }
            return id
        },
        formatElderDate(date){
            return moment(date).format('YYYY-MM-DD HH:mm:ss')
        },
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms))
        },
        getWriteLabel(writePriorityArray){
            let index=1
            for (let item of writePriorityArray){
                if(item!==null){
                    if(typeof(item) === "object"){
                        let deadline=item.deadline ? item.deadline : ""
                        return item.value+ " @ "+index+" "+this.formatElderDate(deadline)
                    } else {
                        return item+ " @ "+index
                    }
                }
                index=index+1
            }
            return ""
        },
        async refresh() {
            this.error=null
            let data = await callAPI(
                "latest-readings/",
                {latest_only: true},
                'post'
            )
            let controlPoints = JSON.parse(JSON.stringify(this.writablePoints))
            if(data&&data.data&&data.data.result&&data.data.result.length>0){
                    let updatedData = _.filter(
                        data.data.result,
                        function (x) {
                            return x.meta && x.meta.tags && x.meta.tags.includes('writable')
                        }
                    )
                    for (let updatedPoint of updatedData){
                        for (let point of controlPoints){
                            if (point.sensor_id===updatedPoint.sensor_id){
                                point.value=updatedPoint.value
                                point.meta=updatedPoint.meta
                            }
                        }
                    }
            }
            this.controlPoints=controlPoints
            this.loading = false
        },
        syncSearches() {
            this.search2 = this.search
        },
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => callback(), wait);
        },
        editItem(item){
            this.editedItem=JSON.parse(JSON.stringify(item))
            this.editedItem.priority=8
            this.editedItem.duration=null
            this.editDialog=true
        },
        async clearPriority(sensor_id,priority){
            await putOptimizationControl({
                'sensor_id': sensor_id,
                'value': null,
                'priority': priority,
            })
            this.editDialog = false
            this.$store.dispatch('callSnackbar', {text: this.$options.filters.translate('Update request sent')})
            this.refresh()
        },
        async updateControl(sensor_id, value, priority,duration) {
            await putOptimizationControl({
                'sensor_id': sensor_id,
                'value': value===""||value==="null" ? null : value,
                'priority': priority&&priority!=="" ? priority : 8,
                'duration':duration,
            })
            this.editDialog = false
            this.$store.dispatch('callSnackbar', {text: this.$options.filters.translate('Update request sent')})
            this.refresh()
        }
    }
}
</script>

<style scoped>

</style>
